<div class="w-full bg-white border border-[#e5e7eb] relative text-slate-900 px-4 h-full flex flex-col overflow-hidden">
  <div class="flex items-center justify-between pt-5 pb-3 border-b border-gray-200 mb-2 shrink-0">
    <h1 class="text-base font-bold text-gray-900 m-0">Session details</h1>
    <button
      type="button"
      class="bg-white outline-none border-none !bg-transparent hover:bg-gray-50 text-gray-400 hover:text-gray-600 !w-8 !h-8 !rounded-md cursor-pointer"
      (click)="closeDrawer()"
      aria-label="Close details">
      <span class="material-symbols-outlined !text-lg">close</span>
    </button>
  </div>

  <div class="flex-1 overflow-y-auto pb-4 mt-0 pr-1" *ngIf="session">
    <div class="mb-4">
      <p class="text-[13px] text-[#808080] mb-2 leading-none font-medium">Status</p>
      <span
        [ngClass]="getStatusClass(session.status)"
        class="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[12px] font-semibold border whitespace-nowrap">
        <span class="material-symbols-outlined !text-[14px]">{{ getStatusIcon(session.status) }}</span>
        {{ getStatusLabel(session.status) }}
      </span>
    </div>

    <div class="access-path mb-5">
      <p class="text-[13px] text-[#808080] mb-3 leading-none font-medium">Access path</p>
      <div class="path-step">
        <span class="path-label">Actor</span>
        <div class="person-cell">
          <div class="person-avatar-wrap">
            <img *ngIf="hasProfileImage(resolvedActorUser)" [src]="getProfileImageUrl(resolvedActorUser)" class="person-avatar" alt="" (error)="markAvatarBroken($event)" />
            <ngx-avatars *ngIf="!hasProfileImage(resolvedActorUser)" [name]="actorName" [initialsSize]="2" [textSizeRatio]="3" size="28"></ngx-avatars>
          </div>
          <span class="path-value">{{ actorName }}</span>
        </div>
      </div>
      <div class="path-connector" aria-hidden="true"></div>
      <div class="path-step">
        <span class="path-label">Action</span>
        <span class="path-value inline-flex items-center gap-1.5">
          <span class="material-symbols-outlined text-[18px] text-[#4a4a51]">{{ getEventIcon(session.latestEventType) }}</span>
          {{ getEventLabel(session.latestEventType) }}
        </span>
      </div>
      <div class="path-connector" aria-hidden="true"></div>
      <div class="path-step">
        <span class="path-label">Target</span>
        <div class="person-cell">
          <div class="person-avatar-wrap">
            <img *ngIf="hasProfileImage(resolvedEmployeeUser)" [src]="getProfileImageUrl(resolvedEmployeeUser)" class="person-avatar" alt="" (error)="markAvatarBroken($event)" />
            <ngx-avatars *ngIf="!hasProfileImage(resolvedEmployeeUser)" [name]="employeeName" [initialsSize]="2" [textSizeRatio]="3" size="28"></ngx-avatars>
          </div>
          <span class="path-value">{{ employeeName }}</span>
        </div>
      </div>
      <div class="path-connector" aria-hidden="true"></div>
      <div class="path-step">
        <span class="path-label">Session</span>
        <span class="path-value path-mono" [matTooltip]="session.sessionId">{{ session.sessionId }}</span>
      </div>
      <div class="path-connector" aria-hidden="true"></div>
      <div class="path-step">
        <span class="path-label">Result</span>
        <span class="path-value">{{ getStatusLabel(session.status) }}</span>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-y-4 gap-x-4 mb-6">
      <div>
        <p class="text-[13px] text-[#808080] mb-2 leading-none font-medium">Employee</p>
        <div class="person-cell">
          <div class="person-avatar-wrap">
            <img *ngIf="hasProfileImage(resolvedEmployeeUser)" [src]="getProfileImageUrl(resolvedEmployeeUser)" class="person-avatar" alt="" (error)="markAvatarBroken($event)" />
            <ngx-avatars *ngIf="!hasProfileImage(resolvedEmployeeUser)" [name]="employeeName" [initialsSize]="2" [textSizeRatio]="3" size="28"></ngx-avatars>
          </div>
          <span class="leading-none text-[15px] font-medium text-gray-800">{{ employeeName }}</span>
        </div>
      </div>
      <div>
        <p class="text-[13px] text-[#808080] mb-2 leading-none font-medium">Accessed by</p>
        <div class="person-cell">
          <div class="person-avatar-wrap">
            <img *ngIf="hasProfileImage(resolvedActorUser)" [src]="getProfileImageUrl(resolvedActorUser)" class="person-avatar" alt="" (error)="markAvatarBroken($event)" />
            <ngx-avatars *ngIf="!hasProfileImage(resolvedActorUser)" [name]="actorName" [initialsSize]="2" [textSizeRatio]="3" size="28"></ngx-avatars>
          </div>
          <span class="leading-none text-[15px] font-medium text-gray-800">{{ actorName }}</span>
        </div>
      </div>
      <div>
        <p class="text-[13px] text-[#808080] mb-2 leading-none font-medium">Started</p>
        <div class="flex items-center gap-2">
          <span class="material-symbols-outlined text-lg text-[#4a4a51] leading-none">schedule</span>
          <span class="leading-none text-[15px] font-medium text-gray-800">{{ formatDateTime(session.startedAtUtc) }}</span>
        </div>
      </div>
      <div>
        <p class="text-[13px] text-[#808080] mb-2 leading-none font-medium">Ended</p>
        <div class="flex items-center gap-2">
          <span class="material-symbols-outlined text-lg text-[#4a4a51] leading-none">event_busy</span>
          <span class="leading-none text-[15px] font-medium text-gray-800">
            {{ formatDateTime(session.endedAtUtc || session.failedAtUtc || session.droppedAtUtc) }}
          </span>
        </div>
      </div>
      <div>
        <p class="text-[13px] text-[#808080] mb-2 leading-none font-medium">Duration</p>
        <div class="flex items-center gap-2">
          <span class="material-symbols-outlined text-lg text-[#4a4a51] leading-none">timer</span>
          <span class="leading-none text-[15px] font-medium text-gray-800">{{ formatDuration() }}</span>
        </div>
      </div>
      <div>
        <p class="text-[13px] text-[#808080] mb-2 leading-none font-medium">Session ID</p>
        <div class="flex items-center gap-2 min-w-0">
          <span class="material-symbols-outlined text-lg text-[#4a4a51] leading-none shrink-0">tag</span>
          <span class="leading-none text-[13px] font-medium text-gray-800 break-all">{{ session.sessionId }}</span>
        </div>
      </div>
    </div>

    <div class="mb-6" *ngIf="session.endReason || session.failureStage || session.errorMessage || networkSummary()">
      <h2 class="text-[15px] font-bold text-gray-900 m-0 mb-3 pt-4 border-t border-gray-200">Technical details</h2>
      <div class="grid grid-cols-1 gap-3">
        <div *ngIf="session.endReason">
          <p class="text-[13px] text-[#808080] mb-1 leading-none font-medium">Reason</p>
          <p class="text-[14px] font-medium text-gray-800 m-0">{{ formatReason(session.endReason) }}</p>
        </div>
        <div *ngIf="session.failureStage">
          <p class="text-[13px] text-[#808080] mb-1 leading-none font-medium">Failure stage</p>
          <p class="text-[14px] font-medium text-gray-800 m-0">{{ formatReason(session.failureStage) }}</p>
        </div>
        <div *ngIf="session.errorMessage">
          <p class="text-[13px] text-[#808080] mb-1 leading-none font-medium">Error</p>
          <p class="text-[14px] font-medium text-gray-800 m-0 break-words">{{ session.errorMessage }}</p>
        </div>
        <div *ngIf="networkSummary() as network">
          <p class="text-[13px] text-[#808080] mb-1 leading-none font-medium">Network</p>
          <p class="text-[14px] font-medium text-gray-800 m-0">{{ network }}</p>
        </div>
      </div>
    </div>

    <div *ngIf="timelineEvents().length">
      <h2 class="text-[15px] font-bold text-gray-900 m-0 mb-4 pt-4 border-t border-gray-200">Event timeline</h2>
      <div class="flex flex-col relative gap-4">
        <div class="flex items-start gap-3" *ngFor="let event of timelineEvents()">
          <div class="flex flex-col items-center w-[28px] shrink-0 relative pt-[2px]">
            <span class="material-symbols-outlined !text-[18px] text-[#5459d1]">{{ getEventIcon(event.event_type) }}</span>
          </div>
          <div class="flex flex-col items-start gap-1 min-w-0">
            <p class="!text-[14px] font-medium text-gray-900 leading-tight m-0">{{ getEventLabel(event.event_type) }}</p>
            <p class="!text-[13px] text-[#808080] m-0">{{ formatDateTime(event.event_time_utc) }}</p>
            <p class="!text-[12px] text-[#6b7280] m-0" *ngIf="event.end_reason">{{ formatReason(event.end_reason) }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="shrink-0 border-t border-gray-200 py-3 bg-white" *ngIf="session?.canViewScreencast">
    <button
      mat-raised-button
      type="button"
      class="view-screencast-btn"
      (click)="viewScreencast()">
      <span class="material-symbols-outlined text-lg">screen_share</span>
      <span>View Screencast</span>
    </button>
  </div>
</div>
